import type { LegalDoc } from './types';

export const refundPolicy: LegalDoc = {
  slug: 'refunds',
  title: 'Refund and Cancellation Policy',
  effectiveDate: '2026-06-15',
  lastUpdated: '2026-10-11',
  intro:
    `This Refund and Cancellation Policy explains when and how you can cancel a Sansara subscription and when you are eligible for a refund. It forms part of our Terms of Service. Our subscription payments are processed by our payment partner, Razorpay; approved refunds are returned through the same channel used for payment. We have tried to make this policy fair, clear and easy to understand.`,
  sections: [
    {
      heading: 'What You Are Paying For',
      paragraphs: [
        `Sansara offers paid subscription tiers that unlock additional features of an educational and factual market-information and portfolio-tracking platform. You are paying for access to software features and content for the duration of your billing cycle. You are not paying for, and Sansara does not provide, investment advice, recommendations or any assurance of investment returns. Subscriptions are billed either monthly or annually, as selected at checkout, in Indian Rupees, through Razorpay.`,
      ],
    },
    {
      heading: 'Cooling-Off Refund for Annual Plans',
      paragraphs: [
        `If you purchase an annual subscription, you may request a full refund within seven (7) days of the date of purchase or renewal (the "cooling-off period"), provided you have not materially used premium features beyond reasonable evaluation. To request a cooling-off refund, contact support@sansarafin.com from your registered email within the seven-day window. On approval, the full amount paid for that annual term will be refunded and your premium access will end.`,
        `After the seven-day cooling-off period, annual subscriptions are non-refundable for the remainder of the term, except where a refund is required by applicable law or is approved by us at our discretion in exceptional circumstances.`,
      ],
    },
    {
      heading: 'Monthly Plans — No Pro-Rata Refunds',
      paragraphs: [
        `Monthly subscriptions are non-refundable, and we do not provide pro-rata refunds for the unused portion of a monthly billing cycle. If you cancel a monthly plan, your premium access continues until the end of the current paid month and then stops; you will not be charged again. This approach keeps monthly pricing simple and is stated clearly here so there is no surprise.`,
      ],
    },
    {
      heading: 'How to Cancel',
      paragraphs: [
        `You can cancel your subscription at any time from Settings within the app, or by emailing support@sansarafin.com. Cancellation stops future automatic renewals. Cancelling does not, by itself, trigger a refund; refund eligibility is governed by the sections above. For subscriptions purchased through the Apple App Store, you must manage and cancel the subscription through your Apple ID subscription settings, and Apple's refund policies will also apply to those purchases.`,
      ],
    },
    {
      heading: 'Auto-Renewal',
      paragraphs: [
        `Subscriptions renew automatically at the end of each billing cycle at the then-current price for that tier, unless you cancel before the renewal date. We will, where required, notify you before an annual renewal. If you do not wish to renew, please cancel before the renewal date to avoid being charged.`,
      ],
    },
    {
      heading: 'Failed, Duplicate or Incorrect Charges',
      paragraphs: [
        `If you are charged more than once for the same subscription, charged after valid cancellation, or charged due to a clear technical error, we will refund the erroneous amount in full once verified. Please report such issues to support@sansarafin.com within thirty (30) days of the charge, with the transaction reference, so we can investigate with Razorpay and resolve it promptly.`,
      ],
    },
    {
      heading: 'How Refunds Are Processed',
      paragraphs: [
        `Approved refunds are issued to the original payment method through Razorpay. Once we approve and initiate a refund, it typically reflects in your account within five (5) to ten (10) business days, depending on your bank or payment provider. We will confirm by email when a refund has been initiated. We do not charge a processing fee for refunds we approve.`,
      ],
    },
    {
      heading: 'Non-Refundable Situations',
      paragraphs: [
        `Refunds are not available in the following situations, except where required by law:`,
      ],
      bullets: [
        `monthly subscriptions, and the unused portion of any monthly cycle;`,
        `annual subscriptions after the seven-day cooling-off period;`,
        `accounts suspended or terminated for breach of our Terms of Service;`,
        `dissatisfaction based on investment outcomes, since Sansara provides no advice and guarantees no returns;`,
        `change of mind after material use of premium features within the cooling-off window.`,
      ],
    },
    {
      heading: 'Price Changes',
      paragraphs: [
        `We may change subscription prices or the features included in a tier on a prospective basis. Any change will not affect a billing cycle you have already paid for. If we increase the price of your plan, we will notify you before the next renewal so you can decide whether to continue.`,
      ],
    },
    {
      heading: 'Consumer Rights and Grievances',
      paragraphs: [
        `Nothing in this policy limits your rights under the Consumer Protection Act, 2019 or other applicable law. If you are unhappy with a refund decision, you may escalate to our Grievance Officer: Sansara Finotech Private Limited, grievance@sansarafin.com, Kanpur, Uttar Pradesh, India. We acknowledge complaints within twenty-four (24) hours and aim to resolve them within fifteen (15) days. See our Grievance Redressal Policy for the full escalation path.`,
      ],
    },
    {
      heading: 'Contact',
      paragraphs: [
        `For all cancellation and refund requests, write to support@sansarafin.com from your registered email address, and include your name, registered email/mobile and the transaction reference so we can locate your subscription quickly.`,
      ],
    },
  ],
};
